// Pill-shaped floating callouts and small in-world prop labels, drawn on
// the Phaser canvas. Match the IONIX reference: rounded rectangle with
// optional eyebrow + main label + icon + downward tail.

import Phaser from 'phaser';
import { brand, brandHex } from '../brand';

export type CalloutKind = 'found' | 'carrying' | 'exposed' | 'info';

interface Palette {
  bg: number;
  fg: string;
  eyebrow: string;
  border: number;
}

const palettes: Record<CalloutKind, Palette> = {
  found:    { bg: brandHex.bgDeep,    fg: brand.textOnDark, eyebrow: brand.asset,   border: brandHex.bgDeep },
  carrying: { bg: brandHex.bgPanel,   fg: brand.text,       eyebrow: brand.primary, border: brandHex.primarySoft },
  exposed:  { bg: brandHex.assetSoft, fg: brand.text,       eyebrow: brand.warn,    border: brandHex.asset },
  info:     { bg: brandHex.bgPanel,   fg: brand.primary,    eyebrow: brand.primary, border: brandHex.primarySoft },
};

const defaultEyebrow: Record<CalloutKind, string> = {
  found: 'ASSET FOUND',
  carrying: 'CARRYING',
  exposed: 'EXPOSED',
  info: 'INFO',
};

export interface MakeCalloutOpts {
  kind?: CalloutKind;
  eyebrow?: string;
  label: string;
  iconKey?: string;
}

/**
 * Floating callout above a sprite. The container's origin is the tip of the
 * tail — so position it at the entity's head (y - sprite half-height) and
 * the body sits above with the tail pointing down at it.
 */
export const makeCallout = (
  scene: Phaser.Scene,
  x: number,
  y: number,
  opts: MakeCalloutOpts,
): Phaser.GameObjects.Container => {
  const kind = opts.kind ?? 'info';
  const palette = palettes[kind];
  const eyebrow = opts.eyebrow ?? defaultEyebrow[kind];

  const container = scene.add.container(x, y).setDepth(25);

  const eyebrowText = scene.add.text(0, 0, eyebrow, {
    fontFamily: '"Inter", system-ui, sans-serif',
    fontSize: '8px',
    fontStyle: '700',
    color: palette.eyebrow,
  }).setOrigin(0, 0);

  const labelText = scene.add.text(0, 0, opts.label, {
    fontFamily: '"Inter", system-ui, sans-serif',
    fontSize: '12px',
    fontStyle: '700',
    color: palette.fg,
  }).setOrigin(0, 0);

  const iconSize = 16;
  const padX = 8;
  const padY = 5;
  const gap = opts.iconKey ? 6 : 0;
  const textBlockW = Math.max(eyebrowText.width, labelText.width);
  const textBlockH = eyebrowText.height + 1 + labelText.height;
  const iconW = opts.iconKey ? iconSize : 0;
  const innerW = iconW + gap + textBlockW;
  const totalW = innerW + padX * 2;
  const totalH = Math.max(textBlockH, iconSize) + padY * 2;

  const bg = scene.add.graphics();
  bg.fillStyle(palette.bg, 1);
  bg.fillRoundedRect(-totalW / 2, -totalH - 6, totalW, totalH, 8);
  bg.lineStyle(1, palette.border, 1);
  bg.strokeRoundedRect(-totalW / 2, -totalH - 6, totalW, totalH, 8);
  container.add(bg);

  if (opts.iconKey) {
    const icon = scene.add.sprite(-totalW / 2 + padX, -totalH / 2 - 6, opts.iconKey)
      .setOrigin(0, 0.5)
      .setDisplaySize(iconSize, iconSize);
    container.add(icon);
  }

  const textX = -totalW / 2 + padX + iconW + gap;
  const textY = -totalH - 6 + padY;
  eyebrowText.setPosition(textX, textY);
  labelText.setPosition(textX, textY + eyebrowText.height + 1);
  container.add([eyebrowText, labelText]);

  // Tail: triangle pointing down to (0, 0)
  const tail = scene.add.graphics();
  tail.fillStyle(palette.bg, 1);
  tail.lineStyle(1, palette.border, 1);
  tail.beginPath();
  tail.moveTo(-5, -6);
  tail.lineTo(5, -6);
  tail.lineTo(0, 0);
  tail.closePath();
  tail.fillPath();
  tail.strokePath();
  container.add(tail);

  return container;
};

/** Small pill label next to a prop (e.g. "SCANNER"). Centred at (x, y). */
export const makePropLabel = (
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
): Phaser.GameObjects.Container => {
  const container = scene.add.container(x, y).setDepth(15);

  const t = scene.add.text(0, 0, text, {
    fontFamily: '"Inter", system-ui, sans-serif',
    fontSize: '8px',
    fontStyle: '700',
    color: brand.primary,
  }).setOrigin(0.5);

  const padX = 6;
  const padY = 3;
  const totalW = t.width + padX * 2;
  const totalH = t.height + padY * 2;

  const bg = scene.add.graphics();
  bg.fillStyle(brandHex.bgPanel, 1);
  bg.fillRoundedRect(-totalW / 2, -totalH / 2, totalW, totalH, 4);
  bg.lineStyle(1, brandHex.primarySoft, 1);
  bg.strokeRoundedRect(-totalW / 2, -totalH / 2, totalW, totalH, 4);

  container.add([bg, t]);
  return container;
};
